import React,{useRef,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './AssetLibraryScreen.css';

const {
  FiArrowRight,
  FiImage,
  FiPlus,
  FiTrash2,
  FiUploadCloud,
  FiX
}=FiIcons;

const sourceLabels={
  logo:'Brand logo',
  'post-upload':'Post upload',
  manual:'Library upload'
};

function AssetCard({asset,onSelect}) {
  return (
    <motion.button
      type="button"
      className="asset-card"
      onClick={()=> onSelect(asset)}
      initial={{opacity:0,y:12}}
      animate={{opacity:1,y:0}}
      layout
    >
      <span className="asset-thumbnail">
        <img src={asset.imageUrl} alt="" />
        <span>{sourceLabels[asset.source] || 'Brand asset'}</span>
      </span>
      <span className="asset-card-copy">
        <strong>{asset.label}</strong>
        <small>{asset.addedDate}</small>
      </span>
    </motion.button>
  );
}

function AssetPreview({asset,brand,onClose,onUse,onRemove}) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <motion.section
        className="asset-preview-modal"
        onMouseDown={(event)=> event.stopPropagation()}
        initial={{opacity:0,scale:0.96}}
        animate={{opacity:1,scale:1}}
      >
        <button
          type="button"
          className="asset-preview-close"
          aria-label="Close asset preview"
          onClick={onClose}
        >
          <SafeIcon icon={FiX} />
        </button>

        <div className="asset-preview-image">
          <img src={asset.imageUrl} alt={asset.label} />
        </div>

        <div className="asset-preview-details">
          <span className="eyebrow">
            {sourceLabels[asset.source] || 'Brand asset'}
          </span>
          <h2>{asset.label}</h2>
          <p>Added {asset.addedDate} to {brand.name}.</p>

          <div className="asset-preview-actions">
            <button
              type="button"
              className="asset-remove"
              onClick={()=> onRemove(asset.id)}
            >
              <SafeIcon icon={FiTrash2} />
              Remove
            </button>
            <button
              type="button"
              className="primary-button"
              style={{background:brand.primary}}
              onClick={()=> onUse(asset)}
            >
              Use in new post
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </div>
      </motion.section>
    </div>
  );
}

function AssetLibraryScreen({
  brand,
  assets,
  loading,
  error,
  onAdd,
  onRemove,
  onUse
}) {
  const [selectedAsset,setSelectedAsset]=useState(null);
  const [uploading,setUploading]=useState(false);
  const [uploadError,setUploadError]=useState(null);
  const inputRef=useRef(null);

  const chooseFile=async (event)=> {
    const file=event.target.files?.[0];
    event.target.value='';

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      await onAdd(file);
    } catch (uploadErr) {
      setUploadError(uploadErr?.message || 'Failed to upload asset.');
    } finally {
      setUploading(false);
    }
  };

  const removeAsset=async (assetId)=> {
    await onRemove(assetId);
    setSelectedAsset(null);
  };

  const displayError=error || uploadError;

  return (
    <motion.main
      className="page asset-library-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <header className="asset-library-header">
        <div>
          <span className="eyebrow">{brand.name}</span>
          <h1>Asset library</h1>
          <p>
            Keep approved logos and reusable imagery close for the next post.
          </p>
        </div>

        <button
          type="button"
          className="primary-button"
          style={{background:brand.primary}}
          disabled={uploading}
          onClick={()=> inputRef.current?.click()}
        >
          <SafeIcon icon={FiPlus} />
          {uploading ? 'Uploading…' : 'Add asset'}
        </button>
        <input
          ref={inputRef}
          className="asset-file-input"
          type="file"
          accept="image/*"
          onChange={chooseFile}
        />
      </header>

      {displayError && (
        <p className="asset-library-error" role="alert">{displayError}</p>
      )}

      <div className="asset-library-toolbar">
        <span>{assets.length} {assets.length===1 ? 'asset' : 'assets'}</span>
        <span>Saved to your workspace</span>
      </div>

      {loading ? (
        <section className="asset-empty">
          <p>Loading assets…</p>
        </section>
      ) : assets.length ? (
        <section className="asset-grid">
          {assets.map((asset)=> (
            <AssetCard
              key={asset.id}
              asset={asset}
              onSelect={setSelectedAsset}
            />
          ))}
        </section>
      ) : (
        <section className="asset-empty">
          <span>
            <SafeIcon icon={FiImage} />
          </span>
          <h2>A calm place for your brand imagery.</h2>
          <p>
            Add a logo, photo, or reusable background for {brand.name}.
          </p>
          <button
            type="button"
            className="secondary-button"
            onClick={()=> inputRef.current?.click()}
          >
            <SafeIcon icon={FiUploadCloud} />
            Upload your first asset
          </button>
        </section>
      )}

      {selectedAsset && (
        <AssetPreview
          asset={selectedAsset}
          brand={brand}
          onClose={()=> setSelectedAsset(null)}
          onUse={onUse}
          onRemove={removeAsset}
        />
      )}
    </motion.main>
  );
}

export default AssetLibraryScreen;